// Release history shown on the What's new page (fleet i18n standard §6 — bilingual patch notes).
// Shape copied from ptc-statetax's `patchNotesData.ts`, the fleet reference.
//
// Shape only lives here — version, release date and the *kind* of each change.
// Every user-facing string lives in i18n (en.json / es.json) under
// `patchNotesPage.releases.<release key>`:
//
//   patchNotesPage.releases.<key>.title
//   patchNotesPage.releases.<key>.changes.<change key>
//
// Dates are ISO (YYYY-MM-DD) and are formatted per locale at render time —
// same scope fence as the rest of the app (i18n handles chrome, Intl handles dates).
//
// ONE RELEASE PER DEPLOY. Add a new release AT THE TOP of `patchReleases`, then add the matching
// title/changes keys to BOTH locale files. `patchNotesData.test.ts` fails the build if a key is
// missing from either locale or if the list falls out of order. Write for the person using the
// screen: what changed for them, in the screen's own words — never internal names.

export type PatchChangeType = 'feature' | 'improvement' | 'fix';

export interface PatchChange {
  /** Leaf key under `patchNotesPage.releases.<release key>.changes`. */
  key: string;
  type: PatchChangeType;
}

export interface PatchRelease {
  /** Semver label shown as `v1.2.3`. */
  version: string;
  /** ISO date (YYYY-MM-DD) the release reached staff. */
  date: string;
  /** Group key under `patchNotesPage.releases`. */
  key: string;
  changes: PatchChange[];
}

/** Newest first. */
export const patchReleases: PatchRelease[] = [
  {
    version: '1.24.0',
    date: '2026-11-04',
    key: 'payrollSource',
    changes: [
      { key: 'template', type: 'feature' },
      { key: 'journal', type: 'feature' },
      { key: 'contractor', type: 'feature' },
      { key: 'guide', type: 'improvement' },
    ],
  },
  {
    version: '1.23.0',
    date: '2026-11-04',
    key: 'chartImport',
    changes: [
      { key: 'import', type: 'feature' },
      { key: 'usedForGl', type: 'feature' },
      { key: 'sides', type: 'improvement' },
      { key: 'uploads', type: 'improvement' },
    ],
  },
  {
    version: '1.22.0',
    date: '2026-11-04',
    key: 'gratuityLine',
    changes: [
      { key: 'notes', type: 'feature' },
      { key: 'standard', type: 'improvement' },
      { key: 'split', type: 'feature' },
    ],
  },
  {
    version: '1.21.1',
    date: '2026-11-02',
    key: 'registryFinal',
    changes: [{ key: 'creditRefund', type: 'improvement' }],
  },
  {
    version: '1.21.0',
    date: '2026-11-02',
    key: 'oneStepTemplate',
    changes: [
      { key: 'noDuplicates', type: 'fix' },
      { key: 'picker', type: 'improvement' },
      { key: 'oneStep', type: 'improvement' },
      { key: 'autofill', type: 'feature' },
      { key: 'part', type: 'feature' },
      { key: 'attachments', type: 'feature' },
    ],
  },
  {
    version: '1.20.0',
    date: '2026-11-02',
    key: 'gradesAndRemoved',
    changes: [
      { key: 'removed', type: 'feature' },
      { key: 'badge', type: 'improvement' },
      { key: 'grades', type: 'feature' },
      { key: 'screens', type: 'fix' },
    ],
  },
  {
    version: '1.19.1',
    date: '2026-10-29',
    key: 'viewInPage',
    changes: [
      { key: 'inPage', type: 'improvement' },
      { key: 'salesCheck', type: 'feature' },
    ],
  },
  {
    version: '1.19.0',
    date: '2026-10-29',
    key: 'viewDocument',
    changes: [{ key: 'view', type: 'feature' }],
  },
  {
    version: '1.18.0',
    date: '2026-10-29',
    key: 'clientTemplate',
    changes: [
      { key: 'template', type: 'feature' },
      { key: 'posJournal', type: 'feature' },
      { key: 'addLine', type: 'improvement' },
      { key: 'codes', type: 'feature' },
      { key: 'archive', type: 'improvement' },
    ],
  },
  {
    version: '1.17.0',
    date: '2026-10-29',
    key: 'openInANewTab',
    changes: [{ key: 'realLinks', type: 'improvement' }],
  },
  {
    version: '1.16.2',
    date: '2026-10-29',
    key: 'posFiguresKept',
    changes: [{ key: 'posFigures', type: 'fix' }],
  },
  {
    version: '1.16.1',
    date: '2026-10-28',
    key: 'adminsSeeTheirTools',
    changes: [
      { key: 'adminCheck', type: 'fix' },
      { key: 'glColumns', type: 'fix' },
      { key: 'copy', type: 'improvement' },
    ],
  },
  {
    version: '1.16.0',
    date: '2026-10-28',
    key: 'yourClientAtAGlance',
    changes: [
      { key: 'overview', type: 'feature' },
      { key: 'bankStatementsOnly', type: 'improvement' },
      { key: 'sourcesInJournal', type: 'feature' },
      { key: 'fullWidth', type: 'improvement' },
      { key: 'navOrder', type: 'improvement' },
    ],
  },
  {
    version: '1.15.0',
    date: '2026-10-28',
    key: 'findYourWay',
    changes: [
      { key: 'headerSearch', type: 'improvement' },
      { key: 'journalsFirst', type: 'improvement' },
      { key: 'backToWork', type: 'feature' },
      { key: 'accuracyForClient', type: 'improvement' },
      { key: 'dr15Upload', type: 'fix' },
      { key: 'signOut', type: 'feature' },
      { key: 'glOneList', type: 'feature' },
    ],
  },
  {
    version: '1.14.0',
    date: '2026-10-28',
    key: 'yourWork',
    changes: [
      { key: 'workTab', type: 'feature' },
      { key: 'tabInLink', type: 'improvement' },
      { key: 'streakByClient', type: 'feature' },
      { key: 'figuresChecked', type: 'feature' },
    ],
  },
  {
    version: '1.13.0',
    date: '2026-10-28',
    key: 'nothingLost',
    changes: [
      { key: 'journalInLink', type: 'feature' },
      { key: 'newJournalKept', type: 'improvement' },
    ],
  },
  {
    version: '1.12.0',
    date: '2026-10-28',
    key: 'yourChart',
    changes: [
      { key: 'chart', type: 'feature' },
      { key: 'customRows', type: 'feature' },
      { key: 'salesSplit', type: 'feature' },
    ],
  },
  {
    version: '1.11.0',
    date: '2026-10-27',
    key: 'clientStaysPicked',
    changes: [
      { key: 'headerPicker', type: 'feature' },
      { key: 'remembered', type: 'feature' },
    ],
  },
  {
    version: '1.10.0',
    date: '2026-10-27',
    key: 'calendarAndFeedbackLanguage',
    changes: [
      { key: 'calendar', type: 'fix' },
      { key: 'pickedDateSaves', type: 'improvement' },
      { key: 'feedbackLanguage', type: 'improvement' },
    ],
  },
  {
    version: '1.9.0',
    date: '2026-10-27',
    key: 'dr15OneLedger',
    changes: [
      { key: 'paymentToPayable', type: 'improvement' },
      { key: 'dateFromReturn', type: 'improvement' },
      { key: 'unsavedNeverExported', type: 'fix' },
    ],
  },
  {
    version: '1.8.0',
    date: '2026-10-26',
    key: 'accuracyBoard',
    changes: [
      { key: 'howWellItReads', type: 'feature' },
      { key: 'journalErrorsInLanguage', type: 'improvement' },
      { key: 'celebrations', type: 'feature' },
    ],
  },
  {
    version: '1.7.0',
    date: '2026-10-26',
    key: 'inXeroConfirmed',
    changes: [
      { key: 'confirmInXero', type: 'feature' },
      { key: 'correctExported', type: 'feature' },
    ],
  },
  {
    version: '1.6.0',
    date: '2026-10-26',
    key: 'dr15IntoJournal',
    changes: [
      { key: 'dr15FillsJournal', type: 'feature' },
      { key: 'uploadsKept', type: 'improvement' },
      { key: 'oldExportLink', type: 'fix' },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-10-21',
    key: 'glMappingClearer',
    changes: [{ key: 'saysWhatIsMissing', type: 'fix' }],
  },
  {
    version: '1.4.0',
    date: '2026-10-21',
    key: 'xeroCheckFreshness',
    changes: [{ key: 'saysHowFresh', type: 'improvement' }],
  },
  {
    version: '1.3.0',
    date: '2026-10-21',
    key: 'behindTheScenes',
    changes: [{ key: 'checksItsOwnWork', type: 'improvement' }],
  },
  {
    version: '1.2.0',
    date: '2026-10-21',
    key: 'clearerErrors',
    changes: [{ key: 'moreErrorsInSpanish', type: 'improvement' }],
  },
  {
    version: '1.1.0',
    date: '2026-10-21',
    key: 'feedbackButton',
    changes: [{ key: 'reportOrSuggest', type: 'feature' }],
  },
  {
    // The first release with notes. Earlier changes shipped without them, and inventing their
    // history after the fact would be a record nobody can check.
    version: '1.0.0',
    date: '2026-10-21',
    key: 'spanishRemembered',
    changes: [
      { key: 'straightIn', type: 'feature' },
      { key: 'languageFollowsYou', type: 'feature' },
      { key: 'screenReaders', type: 'improvement' },
      { key: 'lastEnglish', type: 'improvement' },
    ],
  },
];
